import {
  Alert,
  Autocomplete,
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { Project, ShowModalProps } from "../types/projectTypes";
import { useCountry } from "../contexts/CountryContext";
import { getCountrySettings } from "../api/countryApi";
import { CountrySettings } from "../types/countryTypes";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { ProjectStatistics } from "../types/projectStatisticsType";
import { getCountTypeTower, getStatistics, getTypeTower } from "../api/projectApi";
import {
  EmployeePosition,
  Positions,
  PositionsSalary,
} from "../types/EmployeeTypes";
import {
  addPositionsSalary,
  deletePositionsSalary,
  getPositionSalaryByProjectId,
  updatePositionsSalary,
} from "../api/parametersApi";
import GeofencePanel from "./GeofencePanel";
import Save from "@mui/icons-material/Save";
import Cancel from "@mui/icons-material/Cancel";
import Edit from "@mui/icons-material/Edit";
import Delete from "@mui/icons-material/Delete";
import axios from "axios";
import { ProjectDayHours } from "../types/projectDayHoursTypes";
import { getProjectDayHours, createProjectDayHours, updateProjectDayHours } from "../api/projectDayHoursApi";

interface ProjectFormProps {
  project: Project | null;
  statisticsProject: ProjectStatistics | null;
  setShowModal: Dispatch<SetStateAction<ShowModalProps>>;
  onSave: (updatedProject: Project) => void;
  isCreating: boolean;
  onCancel: () => void;
  positions: Positions[];
  onSaveTemporarySalaries: (salaries: PositionsSalary[]) => void; // Callback para guardar datos temporales en el padre
  setSnackbarOpen: (x: boolean) => void;
  setSnackbarMessage: (x: string) => void;
}

const performanceGroups: {
  key: string;
  label: string;
  poUnit: string;
  hhUnit: string;
  hmUnit: string;
  poField: keyof Project;
  hhField: keyof Project;
  hmField: keyof Project;
}[] = [
  { key: "civilWorks", label: "Obras Civiles", poUnit: "torres/día", hhUnit: "HH/torre", hmUnit: "HM/torre", poField: "civilWorksPo", hhField: "civilWorksHh", hmField: "civilWorksHm" },
  { key: "assembly", label: "Montaje", poUnit: "torres/día", hhUnit: "HH/torre", hmUnit: "HM/torre", poField: "assemblyPo", hhField: "assemblyHh", hmField: "assemblyHm" },
  { key: "conductorLaying", label: "Tendido Conductor", poUnit: "km/día", hhUnit: "HH/km", hmUnit: "HM/km", poField: "conductorLayingPo", hhField: "conductorLayingHh", hmField: "conductorLayingHm" },
  { key: "opgwLaying", label: "Tendido OPGW", poUnit: "km/día", hhUnit: "HH/km", hmUnit: "HM/km", poField: "opgwLayingPo", hhField: "opgwLayingHh", hmField: "opgwLayingHm" },
];

function TabPanel(props: any) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box p={3}>
          <Grid container spacing={2}>
            {children}
          </Grid>
        </Box>
      )}
    </div>
  );
}

const ProjectForm: React.FC<ProjectFormProps> = ({
  project,
  statisticsProject,
  onSave,
  setShowModal,
  onCancel,
  isCreating,
  positions,
  onSaveTemporarySalaries,
  setSnackbarOpen,
  setSnackbarMessage,
}) => {
  const [formData, setFormData] = useState<Omit<Project, "id">>(
    project || {
      name: "",
      costCenter: "",
      offerBudget: 0,
      budget0: 0,
      voltageKV: 0,
      numberOfCircuits: 0,
      numberOfConductorsPerPhase: 0,
      conductorType: "",
      numberOfOpgw: 0,
      opgwType: "",
      numberOfGuardCables: 0,
      guardCableType: "",
      averageSpanM: 0,
      numberOfInsulatorsPerSuspensionTower: "",
      numberOfInsulatorsPerAnchorTower: "",
      suspensionTowerType: "",
      anchorTowerType: "",
      terminalTowerType: "",
      soilTypes: "",
      concreteCostUf: 0,
      steelCostPerKg: 0,
      subcontractSteelCost: 0,
      spansToMark: "",
      avifaunaSpans: "",
      ltCrossings: "",
      mopAccesses: "",
      towerPainting: "",
      routeCrossings: "",
      subcontractActivity: "",
      subcontractObservations: "",
      poHhKm: 0,
      poHmKm: 0,
      conductorLayingDistribution: 0,
      pilotDriverDistribution: 0,
      civilWorksPo: 0,
      civilWorksHh: 0,
      civilWorksHm: 0,
      assemblyPo: 0,
      assemblyHh: 0,
      assemblyHm: 0,
      conductorLayingPo: 0,
      conductorLayingHh: 0,
      conductorLayingHm: 0,
      opgwLayingPo: 0,
      opgwLayingHh: 0,
      opgwLayingHm: 0,
      suspensionTowerTypeArry: [],
      anchorTowerTypeArrya: [],
      status: true
    }
  );
  const [tabIndex, setTabIndex] = useState(0);
  const [statisticsData, setStatisticsData] = useState<ProjectStatistics | null>(statisticsProject || null);
  const [selectedPosition, setSelectedPosition] = useState<Positions | null>(null);
  const [salary, setSalary] = useState<string>(""); // Ahora se maneja como string para formatear
  const [positionSalaries, setPositionSalaries] = useState<PositionsSalary[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingSalary, setEditingSalary] = useState<string>("");
  const [towerTypes, setTowerTypes] = useState<string[]>([]);
  const [countTypesSuspension, setCountTypesSuspension] = useState<number>();
  const [countTypesAnchor, setCountTypesAnchor] = useState<number>();

  // Multi-país: países disponibles para asignar al proyecto.
  const { countries } = useCountry();

  // Configuración por defecto del país del proyecto (para mostrar qué valor se hereda).
  const [countryDefaults, setCountryDefaults] = useState<CountrySettings | null>(null);
  const projectCountryId = (formData as any).country?.id as number | undefined;

  useEffect(() => {
    if (projectCountryId) {
      getCountrySettings(projectCountryId)
        .then(setCountryDefaults)
        .catch(() => setCountryDefaults(null));
    } else {
      setCountryDefaults(null);
    }
  }, [projectCountryId]);

  const boolLabel = (v?: boolean | null) => (v == null ? "" : v ? "Sí" : "No");

  // Acepta solo números/decimales; vacío => 0 para evitar errores de parseo en el backend.
  const handlePerformanceChange = (field: keyof Project, value: string) => {
    const newValue = value.replace(",", ".").replace(/[^0-9.]/g, "");
    setFormData((prevData) => ({
      ...prevData,
      [field]: newValue.trim() === "" ? 0 : newValue,
    }));
  };

  const DAY_NAMES: Record<number, string> = { 1: "Lunes", 2: "Martes", 3: "Miércoles", 4: "Jueves", 5: "Viernes", 6: "Sábado", 7: "Domingo" };
  const DEFAULT_HOURS: Record<number, number> = { 1: 8, 2: 8, 3: 8, 4: 7, 5: 7, 6: 4, 7: 0 };
  const DAYS_OF_WEEK = [1, 2, 3, 4, 5, 6, 7]; // Lunes a Domingo
  const [dayHoursList, setDayHoursList] = useState<ProjectDayHours[]>(
    DAYS_OF_WEEK.map(day => ({ projectId: project?.id || 0, dayOfWeek: day, hours: DEFAULT_HOURS[day] }))
  );

  // Override booleano con tres estados: heredar del país ('') / Sí / No.
  const renderBoolOverride = (
    label: string,
    field: "overtimeEnabled" | "subcontractEnabled" | "rosterEnabled",
    inheritedDefault?: boolean | null
  ) => {
    const val = (formData as any)[field];
    const strVal = val === true ? "true" : val === false ? "false" : "";
    const inheritLabel =
      inheritedDefault == null
        ? "Heredar del país"
        : `Heredar del país (actualmente: ${boolLabel(inheritedDefault)})`;
    return (
      <Grid item xs={12} md={4}>
        <FormControl fullWidth>
          <InputLabel>{label}</InputLabel>
          <Select
            label={label}
            value={strVal}
            onChange={(e) => {
              const v = e.target.value;
              setFormData((prev) => ({ ...prev, [field]: v === "" ? null : v === "true" }));
            }}
          >
            <MenuItem value="">{inheritLabel}</MenuItem>
            <MenuItem value="true">Sí</MenuItem>
            <MenuItem value="false">No</MenuItem>
          </Select>
        </FormControl>
      </Grid>
    );
  };

  useEffect(() => {
    if (project) {
      setFormData(project);
    } else {
      setFormData({
        name: "",
        costCenter: "",
        offerBudget: 0,
        budget0: 0,
        voltageKV: 0,
        numberOfCircuits: 0,
        numberOfConductorsPerPhase: 0,
        conductorType: "",
        numberOfOpgw: 0,
        opgwType: "",
        numberOfGuardCables: 0,
        guardCableType: "",
        averageSpanM: 0,
        numberOfInsulatorsPerSuspensionTower: "",
        numberOfInsulatorsPerAnchorTower: "",
        suspensionTowerType: "",
        anchorTowerType: "",
        terminalTowerType: "",
        soilTypes: "",
        concreteCostUf: 0,
        steelCostPerKg: 0,
        subcontractSteelCost: 0,
        spansToMark: "",
        avifaunaSpans: "",
        ltCrossings: "",
        mopAccesses: "",
        towerPainting: "",
        routeCrossings: "",
        subcontractActivity: "",
        subcontractObservations: "",
        poHhKm: 0,
        poHmKm: 0,
        conductorLayingDistribution: 0,
        pilotDriverDistribution: 0,
        civilWorksPo: 0,
        civilWorksHh: 0,
        civilWorksHm: 0,
        assemblyPo: 0,
        assemblyHh: 0,
        assemblyHm: 0,
        conductorLayingPo: 0,
        conductorLayingHh: 0,
        conductorLayingHm: 0,
        opgwLayingPo: 0,
        opgwLayingHh: 0,
        opgwLayingHm: 0,
        suspensionTowerTypeArry: [],
        anchorTowerTypeArrya: [],
        status: true
      });
    }
  }, [project]);

  useEffect(() => {
    if (statisticsProject) {
      setStatisticsData(statisticsProject);
      fetchCountTypeTower();
    }
  }, [statisticsProject]);

  useEffect(() => {
    const fetchPositionsSalary = async () => {
      try {
        const data = await getPositionSalaryByProjectId(project?.id || 0);
        setPositionSalaries(data);
      } catch (error) {
        console.error("Error al cargar los datos:", error);
      }
    };

    const fetchTypeTower = async () => {
      try {
        if (project?.id) {
          const typeTower = await getTypeTower(project.id);
          setTowerTypes(typeTower);
        }
      } catch (error) {
        console.error("Error al obtener los tipos de torres " + error)
      }
    }

    const fetchDayHours = async () => {
      try {
        if (project?.id) {
          const data = await getProjectDayHours(project.id);
          if (data.length > 0) {
            setDayHoursList(DAYS_OF_WEEK.map(day => {
              const found = data.find(d => d.dayOfWeek === day);
              return found || { projectId: project.id!, dayOfWeek: day, hours: DEFAULT_HOURS[day] };
            }));
          } else {
            setDayHoursList(DAYS_OF_WEEK.map(day => ({ projectId: project.id!, dayOfWeek: day, hours: DEFAULT_HOURS[day] })));
          }
        }
      } catch (error) {
        console.error("Error al cargar horas día:", error);
      }
    };

    fetchTypeTower();
    fetchPositionsSalary();
    fetchDayHours();
  }, [project]);

  const fetchCountTypeTower = async () => {
    if (project?.id) {
      const response = await getCountTypeTower(
        project.id,
        formData.suspensionTowerTypeArry || [],
        formData.anchorTowerTypeArrya || []
      );

      setCountTypesSuspension(response.suspensionTowerCount);
      setCountTypesAnchor(response.anchorTowerCount);
    }
  };

  // useEffect(() => {
  //    fetchCountTypeTower();
  // }, [project?.id]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Reemplazar "," con "."
    let newValue = value.replace(",", ".");

    // Si el campo es uno de los numéricos y está vacío, asignar "0"
    if (
      name === "pilotDriverDistribution" ||
      name === "conductorLayingDistribution"
    ) {
      newValue = newValue.trim() === "" ? "0" : newValue;
    }

    // Validaciones específicas para ciertos campos numéricos
    if (name === "voltageKV") {
      newValue = newValue.replace(/\D/g, ""); // Permite solo números enteros
    }

    if (
      name === "offerBudget" ||
      name === "budget0" ||
      name === "steelCostPerKg" ||
      name === "subcontractSteelCost"
    ) {
      newValue = newValue.replace(/\D/g, "").replaceAll(".", ""); // Asegura solo números sin puntos
    }

    setFormData((prevData) => ({
      ...prevData,
      [name]: (name === "pilotDriverDistribution" || name === "conductorLayingDistribution")
        ? (newValue.trim() === "" ? 0 : parseFloat(newValue))
        : newValue,
    }));
  };



  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setTabIndex(newValue);
  };

  const handleSave = () => {
    const { name, costCenter } = formData;

    fetchCountTypeTower();

    if (name === "" && costCenter === "") {
      setShowModal({
        show: true,
        message: "Se debe ingresar un nombre de Proyecto y un Centro de Coste",
      });
      return;
    }
    if (name === "") {
      setShowModal({
        show: true,
        message: "Se debe ingresar un nombre de Proyecto",
      });
      return;
    }
    if (costCenter === "") {
      setShowModal({
        show: true,
        message: "Se debe ingresar un Centro de Coste",
      });
      return;
    }
    try {
      onSave(formData);
    } catch (error) {
      console.log("error", error)
    }

    // setFormData({
    //   name: "",
    //   costCenter: "",
    //   offerBudget: 0,
    //   budget0: 0,
    //   voltageKV: 0,
    //   numberOfCircuits: 0,
    //   numberOfConductorsPerPhase: 0,
    //   conductorType: "",
    //   numberOfOpgw: 0,
    //   opgwType: "",
    //   averageSpanM: 0,
    //   numberOfInsulatorsPerSuspensionTower: 0,
    //   numberOfInsulatorsPerAnchorTower: 0,
    //   suspensionTowerType: "",
    //   anchorTowerType: "",
    //   terminalTowerType: "",
    //   soilTypes: "",
    //   concreteCostUf: 0,
    //   steelCostPerKg: 0,
    //   subcontractSteelCost: 0,
    //   spansToMark: "",
    //   avifaunaSpans: "",
    //   ltCrossings: "",
    //   mopAccesses: "",
    //   towerPainting: "",
    //   routeCrossings: "",
    //   subcontractActivity: "",
    //   subcontractObservations: "",
    // });
  };

  const convertKgToTons = (weightInKg: number | undefined): string => {
    if (weightInKg === undefined) return "";
    return (weightInKg / 1000).toFixed(2); // Limitamos a 2 decimales si es necesario
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value);
  };

  const formatCurrencyCLP = (value: number) => {
    return new Intl.NumberFormat("es-CL", {
      style: "currency",
      currency: "CLP",
    }).format(value);
  };

  // Moneda efectiva del proyecto: override del proyecto o, si no, la del país (fallback CLP).
  const effectiveCurrency =
    formData.currency && String(formData.currency).trim() !== ""
      ? String(formData.currency)
      : countryDefaults?.currency || "CLP";

  // Formatea un monto con la moneda efectiva del proyecto/país (no siempre CLP).
  const formatMoney = (value: number | string) => {
    const num =
      typeof value === "number"
        ? value
        : Number(String(value).replace(/[^0-9.-]/g, "")) || 0;
    try {
      return new Intl.NumberFormat("es-CL", { style: "currency", currency: effectiveCurrency }).format(num);
    } catch {
      // Código de moneda no reconocido por Intl: se muestra el código + monto.
      return `${effectiveCurrency} ${num.toLocaleString("es-CL")}`;
    }
  };

  const handleAddPosition = () => {
    if (selectedPosition && salary) {
      const numericSalary = parseInt(salary.replace(/[^0-9]/g, ""), 10);
      const newPositionSalary: PositionsSalary = {
        positionId: selectedPosition,
        salary: numericSalary,
        projectId: project || undefined,
        description: selectedPosition.description || ''
      };

      // Informar al padre de los salarios temporales
      if (!project?.id) {
        onSaveTemporarySalaries([...positionSalaries, newPositionSalary]);
      } else {
        const bodyParams: EmployeePosition = {
          salary: numericSalary,
          projectId: project.id,
          positionId: selectedPosition.id || 0,
          description: selectedPosition.description || "",
        };
        addPositionsSalary(bodyParams);
      }
      setPositionSalaries([...positionSalaries, newPositionSalary]);
      setSelectedPosition(null);
      setSalary("");
      setSnackbarMessage("Cargo creado con éxito");
      setSnackbarOpen(true);
    }
  };

  const handleEditClick = (index: number) => {
    setEditingIndex(index);
    setEditingSalary(formatCurrency(positionSalaries[index].salary || 0));
  };

  const handleSaveEdit = async (index: number) => {
    const numericSalary = parseInt(editingSalary.replace(/[^0-9]/g, ""), 10);
    const updatedItem = { ...positionSalaries[index], salary: numericSalary };
    try {
      if (project?.id) {
        const updatedPositionSalary = await updatePositionsSalary(updatedItem);
        const updatedSalaries = [...positionSalaries];
        updatedSalaries[index] = updatedPositionSalary;
        setPositionSalaries(updatedSalaries);
      } else {
        const updatedSalaries = [...positionSalaries];
        updatedSalaries[index] = updatedItem;
        setPositionSalaries(updatedSalaries);
      }
      setEditingIndex(null);
    } catch (error) {
      console.error("Error al actualizar el salario:", error);
    }
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditingSalary("");
  };

  const handleEditSalary = async (index: number, newSalary: string) => {
    const numericSalary = parseInt(newSalary.replace(/[^0-9]/g, ""), 10); // Convertir a número

    const updatedItem = { ...positionSalaries[index], salary: numericSalary };
    try {
      const updatedPositionSalary = await updatePositionsSalary(updatedItem);
      const updatedSalaries = [...positionSalaries];
      updatedSalaries[index] = updatedPositionSalary;
      setPositionSalaries(updatedSalaries);
    } catch (error) {
      console.error("Error al actualizar el salario:", error);
    }
  };

  const handleDeletePosition = async (id?: number) => {
    if (!id) return;
    try {
      await deletePositionsSalary(id);
      setPositionSalaries(positionSalaries.filter((item) => item.id !== id));
    } catch (error) {
      console.error("Error al eliminar el cargo:", error);
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Tabs
        value={tabIndex}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        aria-label="tabs de proyectos"
        centered={false}
      >
        <Tab label="Información General" />
        <Tab label="Información Estructuras" />
        <Tab label="Información Obra" />
        <Tab label="Información Obra Extraordinarias" />
        <Tab label="Coste personal" />
        <Tab label="Horas semanales" />
        <Tab label="País y configuración" />
        <Tab label="KMZ" />
      </Tabs>


      <TabPanel value={tabIndex} index={0}>
        <Grid item xs={12} md={2.4}>
          <TextField
            label="Nombre Proyecto"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={2.4}>
          <TextField
            label="Centro de Coste"
            name="costCenter"
            value={formData.costCenter}
            onChange={handleInputChange}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={2.4}>
          <TextField
            label={`Presupuesto Oferta (${effectiveCurrency})`}
            name="offerBudget"
            value={
              formData.offerBudget
                ? formatMoney(formData.offerBudget)
                : ""
            }
            onChange={handleInputChange}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={2.4}>
          <TextField
            label={`Presupuesto 0 (${effectiveCurrency})`}
            name="budget0"
            value={formData.budget0 ? formatMoney(formData.budget0) : ""}
            onChange={handleInputChange}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={2.4}>
          <TextField
            label="Tensión (KV)"
            name="voltageKV"
            value={formData.voltageKV}
            onChange={handleInputChange}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={2.4}>
          <TextField
            label="Número de Circuitos"
            name="numberOfCircuits"
            value={formData.numberOfCircuits}
            onChange={handleInputChange}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={2.4}>
          <TextField
            label="Número de Conductores por Fase"
            name="numberOfConductorsPerPhase"
            value={formData.numberOfConductorsPerPhase}
            onChange={handleInputChange}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={2.4}>
          <TextField
            label="Tipo de Conductor"
            name="conductorType"
            value={formData.conductorType}
            onChange={handleInputChange}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={2.4}>
          <TextField
            label="Número de OPGW"
            name="numberOfOpgw"
            value={formData.numberOfOpgw}
            onChange={handleInputChange}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={2.4}>
          <TextField
            label="Tipo de OPGW"
            name="opgwType"
            value={formData.opgwType}
            onChange={handleInputChange}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={2.4}>
          <TextField
            label="Franja Media (m)"
            name="averageSpanM"
            value={formData.averageSpanM}
            onChange={handleInputChange}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={2.4}>
          <TextField
            disabled
            label="Longitud total (km)"
            value={
              statisticsData?.totalLength
                ? parseFloat((statisticsData.totalLength / 1000).toFixed(1))
                : ""
            }
            fullWidth
          />
        </Grid>
        {/* Cable de Guarda (CG). Va tras "Longitud total" siguiendo la maqueta del cliente
            (SGRen- OPGW+CG.pdf). El número gobierna la generación automática de las
            subactividades "Tendido CG / Cable N" y "Riega de Cable Piloto CG / Cable N". */}
        <Grid item xs={12} md={2.4}>
          <TextField
            label="Número de CG"
            name="numberOfGuardCables"
            value={formData.numberOfGuardCables}
            onChange={handleInputChange}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={2.4}>
          <TextField
            label="Tipo de CG"
            name="guardCableType"
            value={formData.guardCableType}
            onChange={handleInputChange}
            fullWidth
          />
        </Grid>
      </TabPanel>

      <TabPanel value={tabIndex} index={1}>
        <Grid item xs={12} md={2.4}>
          <TextField
            disabled
            label="Número de Torres Totales"
            value={statisticsData?.totalTowers || ""}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={2.4}>
          <TextField
            disabled
            label="Número de Torres de Suspensión"
            value={countTypesSuspension}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />

        </Grid>

        <Grid item xs={12} md={2.4}>
          <TextField
            disabled
            label="Número de Torres de Anclaje"
            value={countTypesAnchor}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={2.4}>
          <TextField
            label="Nº Aisladores por Torre Suspensión"
            name="numberOfInsulatorsPerSuspensionTower"
            value={formData.numberOfInsulatorsPerSuspensionTower ?? ""}
            onChange={handleInputChange}
            placeholder="Ej: 3x18"
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={2.4}>
          <TextField
            label="Nº Aisladores por Torre Anclaje"
            name="numberOfInsulatorsPerAnchorTower"
            value={formData.numberOfInsulatorsPerAnchorTower ?? ""}
            onChange={handleInputChange}
            placeholder="Ej: 6x19"
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={2.4}>
          <TextField
            disabled
            label="Peso promedio Estructuras (Tn/Torre)"
            value={
                  statisticsData?.averageWeightStructure
                    ? Number(statisticsData.averageWeightStructure).toFixed(1)
                    : "0.0"
                }
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={2.4}>
          <TextField
            disabled
            label="Peso total (Tn)"
            value={parseFloat(convertKgToTons(statisticsData?.totalWeight)).toFixed(1) || ""}
            fullWidth
          />
        </Grid>
        {/* <Grid item xs={12} md={2.4}>
          <Autocomplete
            multiple
            options={towerTypes}
            value={selectedSuspensionTowerTypes}
            onChange={(event, newValue) => setSelectedSuspensionTowerTypes(newValue)}
            renderInput={(params) => (
              <TextField {...params} label="Tipos de Torre de Suspensión" fullWidth />
            )}
          />
        </Grid>

        <Grid item xs={12} md={2.4}>
          <Autocomplete
            multiple
            options={towerTypes}
            value={selectedAnchorTowerTypes}
            onChange={(event, newValue) => setSelectedAnchorTowerTypes(newValue)}
            renderInput={(params) => (
              <TextField {...params} label="Tipos de Torre de Anclaje" fullWidth />
            )}
          />
        </Grid> */}
        <Grid item xs={12} md={2.4}>
          <Autocomplete
            multiple
            options={towerTypes}
            value={formData.suspensionTowerTypeArry || []}
            onChange={(event, newValue) => {
              setFormData(prev => ({
                ...prev,
                suspensionTowerTypeArry: newValue ?? []
              }));
            }}
            renderInput={(params) => (
              <TextField {...params} label="Tipos de Torre de Suspensión" fullWidth />
            )}
          />
        </Grid>

        <Grid item xs={12} md={2.4}>
          <Autocomplete
            multiple
            options={towerTypes}
            value={formData.anchorTowerTypeArrya || []}
            onChange={(event, newValue) => {
              setFormData(prev => ({
                ...prev,
                anchorTowerTypeArrya: newValue ?? []
              }));
            }}
            renderInput={(params) => (
              <TextField {...params} label="Tipos de Torre de Anclaje" fullWidth />
            )}
          />
        </Grid>

      </TabPanel>

      <TabPanel value={tabIndex} index={2}>
        <Grid item xs={12}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontWeight: "bold", fontSize: "1.1rem", color: "#004F86" }}>
              1
            </Typography>
            <Typography sx={{ fontWeight: "bold", fontSize: "1.1rem" }}>
              PARÁMETROS DE OBRA
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            disabled
            label="Longitud de Caminos Total (m)"
            value={
              statisticsData?.totalPathLength
                ? statisticsData.totalPathLength.toFixed(1)
                : ""
            }
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            disabled
            label="Longitud de Caminos Livianos (m)"
            value={
              statisticsData?.totalLengthLightRoad
                ? statisticsData.totalLengthLightRoad.toFixed(1)
                : ""
            }
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            disabled
            label="Longitud de Caminos Medios (m)"
            value={
              statisticsData?.totalLengthMediumRoad
                ? statisticsData.totalLengthMediumRoad.toFixed(1)
                : ""
            }
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            disabled
            label="Longitud de Caminos Fuertes (m)"
            value={
              statisticsData?.totalLengthHeavyRoad
                ? statisticsData.totalLengthHeavyRoad.toFixed(1)
                : ""
            }
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            disabled
            label="Ratio (km/torre)"
            value={
              statisticsData?.ratioKmPerTower
                ? statisticsData.ratioKmPerTower.toFixed(3)
                : ""
            }
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            disabled
            label="Volumen de Excavación promedio (m3)"
            value={
              statisticsData?.averageExcavation
                ? statisticsData.averageExcavation.toFixed(1)
                : ""
            }
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            disabled
            label="Volumen de Hormigón promedio (m3)"
            value={
              statisticsData?.averageConcrete
                ? statisticsData.averageConcrete.toFixed(1)
                : ""
            }
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            disabled
            label="Peso acero de refuerzo promedio (Tn/torre)"
            value={parseFloat(convertKgToTons(statisticsData?.averageSteel)).toFixed(1) || ""}
            fullWidth
          />
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
            <Typography sx={{ fontWeight: "bold", fontSize: "1.1rem", color: "#004F86" }}>
              2
            </Typography>
            <Typography sx={{ fontWeight: "bold", fontSize: "1.1rem" }}>
              PRECIOS
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            label="Precio m³ Hormigón (UF)"
            name="concreteCostUf"
            value={formData.concreteCostUf}
            onChange={handleInputChange}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            label={`Precio Acero por Kg (${effectiveCurrency})`}
            name="steelCostPerKg"
            value={
              formData.steelCostPerKg
                ? formatMoney(formData.steelCostPerKg)
                : ""
            }
            onChange={handleInputChange}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            label={`Precio Subcontrato Acero (${effectiveCurrency})`}
            name="subcontractSteelCost"
            value={
              formData.subcontractSteelCost
                ? formatMoney(formData.subcontractSteelCost)
                : ""
            }
            onChange={handleInputChange}
            fullWidth
          />
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
            <Typography sx={{ fontWeight: "bold", fontSize: "1.1rem", color: "#004F86" }}>
              3
            </Typography>
            <Typography sx={{ fontWeight: "bold", fontSize: "1.1rem" }}>
              PRODUCCIONES Y RENDIMIENTOS OBJETIVOS
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Grupo de Actividades</TableCell>
                <TableCell align="left" sx={{ fontWeight: "bold" }}>PO</TableCell>
                <TableCell align="left" sx={{ fontWeight: "bold" }}>HH</TableCell>
                <TableCell align="left" sx={{ fontWeight: "bold" }}>HM</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {performanceGroups.map((group) => (
                <TableRow key={group.key}>
                  <TableCell>{group.label}</TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <TextField
                        size="small"
                        placeholder="(manual)"
                        value={(formData as Project)[group.poField] as number | string ?? ""}
                        onChange={(e) => handlePerformanceChange(group.poField, e.target.value)}
                      />
                      <Typography sx={{ whiteSpace: "nowrap" }}>{group.poUnit}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <TextField
                        size="small"
                        placeholder="(manual)"
                        value={(formData as Project)[group.hhField] as number | string ?? ""}
                        onChange={(e) => handlePerformanceChange(group.hhField, e.target.value)}
                      />
                      <Typography sx={{ whiteSpace: "nowrap" }}>{group.hhUnit}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <TextField
                        size="small"
                        placeholder="(manual)"
                        value={(formData as Project)[group.hmField] as number | string ?? ""}
                        onChange={(e) => handlePerformanceChange(group.hmField, e.target.value)}
                      />
                      <Typography sx={{ whiteSpace: "nowrap" }}>{group.hmUnit}</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Grid>
      </TabPanel>

      <TabPanel value={tabIndex} index={3}>
        <Grid item xs={12} md={2}>
          <TextField
            label="Vanos a Balizar"
            name="spansToMark"
            value={formData.spansToMark}
            onChange={handleInputChange}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            label="Vanos Avifauna"
            name="avifaunaSpans"
            value={formData.avifaunaSpans}
            onChange={handleInputChange}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            label="Cruce LT's"
            name="ltCrossings"
            value={formData.ltCrossings}
            onChange={handleInputChange}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            label="Accesos MOP"
            name="mopAccesses"
            value={formData.mopAccesses}
            onChange={handleInputChange}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            label="Pintura Torres"
            name="towerPainting"
            value={formData.towerPainting}
            onChange={handleInputChange}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            label="Cruces Rutas"
            name="routeCrossings"
            value={formData.routeCrossings}
            onChange={handleInputChange}
            fullWidth
          />
        </Grid>
      </TabPanel>

      <TabPanel value={tabIndex} index={4}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth variant="outlined">
              <Autocomplete
                value={selectedPosition}
                onChange={(event, newValue) => setSelectedPosition(newValue)}
                options={positions}
                getOptionLabel={(option) => option.description || ""}
                renderInput={(params) => (
                  <TextField {...params} label="Cargo" variant="outlined" />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="text"
              label="Salario (CLP)"
              variant="outlined"
              value={salary}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, ""); // Permitir solo números
                setSalary(formatCurrencyCLP(Number(value)));
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleAddPosition}
              disabled={!selectedPosition || !salary}
            >
              Agregar
            </Button>
          </Grid>
        </Grid>
        <Typography variant="h6" sx={{ mt: 3 }}>
          Lista de Cargos y Salarios
        </Typography>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: "#004F86", fontWeight: "bold" }}>
                Cargo
              </TableCell>
              <TableCell sx={{ color: "#004F86", fontWeight: "bold" }}>
                Salario (CLP)
              </TableCell>
              <TableCell sx={{ color: "#004F86", fontWeight: "bold" }}>
                Acciones
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {positionSalaries.map((item, index) => (
              <TableRow key={item.id || index}>
                <TableCell>{item.description}</TableCell>
                <TableCell>
                  {editingIndex === index ? (
                    <TextField
                      type="text"
                      value={editingSalary}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, "");
                        setEditingSalary(formatCurrencyCLP(Number(value)));
                      }}
                    />
                  ) : (
                    formatCurrencyCLP(item.salary || 0)
                  )}
                </TableCell>
                <TableCell>
                  {editingIndex === index ? (
                    <>
                      <IconButton
                        color="primary"
                        onClick={() => handleSaveEdit(index)}
                      >
                        <Save />
                      </IconButton>
                      <IconButton color="error" onClick={handleCancelEdit}>
                        Cancelar
                      </IconButton>
                    </>
                  ) : (
                    <>
                      <IconButton
                        color="primary"
                        onClick={() => handleEditClick(index)}
                        disabled={editingIndex !== null}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        color="primary"
                        onClick={() => handleDeletePosition(item.id)}
                      >
                        <Delete />
                      </IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TabPanel>

      <TabPanel value={tabIndex} index={5}>
        <Grid item xs={12}>
          <Typography variant="h6">Configuración de Horas por Día</Typography>
        </Grid>
        <Grid item xs={12}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: "#004F86", fontWeight: "bold" }}>Día</TableCell>
                <TableCell sx={{ color: "#004F86", fontWeight: "bold" }}>Horas</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dayHoursList.map((item, index) => (
                <TableRow key={item.dayOfWeek}>
                  <TableCell>{DAY_NAMES[item.dayOfWeek]}</TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      size="small"
                      value={item.hours}
                      onChange={(e) => {
                        const updated = [...dayHoursList];
                        updated[index] = { ...updated[index], hours: parseFloat(e.target.value) || 0 };
                        setDayHoursList(updated);
                      }}
                      inputProps={{ min: 0, max: 24, step: 0.5 }}
                    />
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell sx={{ fontWeight: "bold", color: "#004F86" }}>Horas Semanales</TableCell>
                <TableCell sx={{ fontWeight: "bold", color: "#004F86" }}>
                  {dayHoursList.reduce((sum, item) => sum + item.hours, 0)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Grid>
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={async () => {
              if (!project?.id) {
                setSnackbarMessage("Debe guardar el proyecto primero");
                setSnackbarOpen(true);
                return;
              }
              try {
                for (const item of dayHoursList) {
                  if (item.id) {
                    await updateProjectDayHours(item);
                  } else {
                    const created = await createProjectDayHours({ ...item, projectId: project.id });
                    setDayHoursList(prev => prev.map(d => d.dayOfWeek === created.dayOfWeek ? created : d));
                  }
                }
                setSnackbarMessage("Horas día guardadas con éxito");
                setSnackbarOpen(true);
              } catch (error) {
                console.error("Error al guardar horas día:", error);
                setSnackbarMessage("Error al guardar horas día");
                setSnackbarOpen(true);
              }
            }}
          >
            Guardar Horas
          </Button>
        </Grid>
      </TabPanel>

      <TabPanel value={tabIndex} index={6}>
        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: "#004F86" }}>
            País del proyecto
          </Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>País</InputLabel>
            <Select
              label="País"
              value={(formData as any).country?.id != null ? String((formData as any).country.id) : ""}
              onChange={(e) => {
                const v = e.target.value;
                setFormData((prev) => ({
                  ...prev,
                  country: v === "" ? null : { id: Number(v) },
                }));
              }}
            >
              {countries.map((c) => (
                <MenuItem key={c.id} value={String(c.id)}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <Alert severity="info" sx={{ mt: 1 }}>
            Estos campos son <strong>excepciones solo para este proyecto</strong>. Si los dejas
            vacíos / en "Heredar", el proyecto usa la <strong>configuración por defecto de su país</strong>
            (que se define en la pantalla <strong>Países</strong>). Entre paréntesis se muestra el valor que
            se heredaría.
          </Alert>
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            label="Moneda (override)"
            fullWidth
            value={formData.currency ?? ""}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, currency: e.target.value === "" ? null : e.target.value }))
            }
            placeholder={countryDefaults ? `Hereda: ${countryDefaults.currency}` : "Heredar del país"}
            InputLabelProps={{ shrink: true }}
            helperText={countryDefaults ? `Por defecto del país: ${countryDefaults.currency}` : " "}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Horas de jornada (override)"
            type="number"
            fullWidth
            value={formData.workdayHours ?? ""}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                workdayHours: e.target.value === "" ? null : Number(e.target.value),
              }))
            }
            placeholder={countryDefaults ? `Hereda: ${countryDefaults.workdayHours}` : "Heredar del país"}
            InputLabelProps={{ shrink: true }}
            helperText={countryDefaults ? `Por defecto del país: ${countryDefaults.workdayHours} h` : " "}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>Separador decimal (override)</InputLabel>
            <Select
              label="Separador decimal (override)"
              value={formData.decimalSeparator ?? ""}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  decimalSeparator: e.target.value === "" ? null : String(e.target.value),
                }))
              }
            >
              <MenuItem value="">
                {countryDefaults
                  ? `Heredar del país (actualmente: ${countryDefaults.decimalSeparator === "," ? "coma" : "punto"})`
                  : "Heredar del país"}
              </MenuItem>
              <MenuItem value=".">Punto ( . )</MenuItem>
              <MenuItem value=",">Coma ( , )</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {renderBoolOverride("Horas extras (override)", "overtimeEnabled", countryDefaults?.overtimeEnabled)}
        {renderBoolOverride("Subcontratas (override)", "subcontractEnabled", countryDefaults?.subcontractEnabled)}
        {renderBoolOverride("Cuadrante de turnos (override)", "rosterEnabled", countryDefaults?.rosterEnabled)}
      </TabPanel>

      <TabPanel value={tabIndex} index={7}>
        <GeofencePanel projectId={project?.id ?? null} />
      </TabPanel>

      <Grid container justifyContent="flex-end" sx={{ gap: 2 }}>
        <Grid item xs={12} md={2} sx={{ mt: 4 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            fullWidth
          >
            {isCreating ? "Crear Proyecto" : "Guardar cambios"}
          </Button>
        </Grid>
        <Grid item xs={12} md={2} sx={{ mt: 4 }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={onCancel}
            fullWidth
          >
            Cancelar
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProjectForm;
